import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { useTranslation } from '../../hooks/useTranslation';
import { useIsPc } from '../../hooks/useIsPc';
import { useMiniProgramLogin } from '../../hooks/useMiniProgramLogin';
import { openMiniProgram } from '../../services/miniProgramLogin';

type Props = { login: ReturnType<typeof useMiniProgramLogin>; agreementAccepted: boolean };

const MiniProgramLogin: React.FC<Props> = ({ login, agreementAccepted }) => {
  const { t } = useTranslation();
  const isPc = useIsPc();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!login.session) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [login.session]);
  const { session, status } = login;
  const terminal = status === 'EXPIRED' || status === 'CANCELLED';
  const remaining = session ? Math.max(0, Math.ceil((session.expiresAt - now) / 1000)) : 0;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center' }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t(isPc ? 'miniLogin.desktopHint' : 'miniLogin.mobileHint')}
      </Typography.Paragraph>
      {session?.qrCodeDataUrl && !terminal && (
        <img src={session.qrCodeDataUrl} alt={t('miniLogin.codeAlt')} style={{ width: 240, maxWidth: '100%' }} />
      )}
      {session && <Typography.Text role="status">{t(`miniLogin.status.${status}`)}</Typography.Text>}
      {session && !terminal && <Typography.Text type="secondary">{t('miniLogin.expires', { seconds: remaining })}</Typography.Text>}
      {login.error && <Alert type="error" showIcon message={t(`miniLogin.errors.${login.error}`, { defaultValue: t('miniLogin.errors.unavailable') })} />}
      {session?.miniProgramUrlLink && !terminal && (
        <Button block disabled={!agreementAccepted} onClick={() => openMiniProgram(session.miniProgramUrlLink!)}>
          {t('miniLogin.reopen')}
        </Button>
      )}
      <Button type="primary" size="large" block loading={login.busy} disabled={!agreementAccepted}
        onClick={() => void login.start(isPc ? 'code' : 'link')}>
        {t(session ? 'miniLogin.refresh' : isPc ? 'miniLogin.generateCode' : 'miniLogin.open')}
      </Button>
    </Space>
  );
};

export default MiniProgramLogin;
